'use client';

import { EcEnv } from '@castlery/config';
import type { User } from '@castlery/types';
import Script from 'next/script';
import { FC, useState, useEffect } from 'react';
import { logger } from '@castlery/observability';

interface YotpoScriptProps {
  getApi?: boolean;
  user: User | null;
}

export const YotpoScript: FC<YotpoScriptProps> = ({ getApi = false, user }) => {
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    const generateToken = async () => {
      if (!user?.email || !EcEnv.NEXT_PUBLIC_YOTPO_API_KEY) {
        setToken('');
        return;
      }

      try {
        // 使用 Web Crypto API 生成 SHA-256 hash
        const encoder = new TextEncoder();
        const data = encoder.encode(user.email.toLowerCase() + EcEnv.NEXT_PUBLIC_YOTPO_API_KEY);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        setToken(hashHex);
      } catch (error) {
        logger.error('Failed to generate Yotpo token', { error, userId: user.id });
        setToken('');
      }
    };

    generateToken();
  }, [user?.email]);

  if (!EcEnv.NEXT_PUBLIC_YOTPO_ENABLED || !EcEnv.NEXT_PUBLIC_YOTPO_GUID) {
    return null;
  }

  return (
    <>
      {user?.id && user?.email && (
        <div
          id="swell-customer-identification"
          data-authenticated="true"
          data-email={user.email}
          data-id={user.id}
          data-tags='["wholesale"]'
          style={{ display: 'none' }}
          data-token={token}
        />
      )}
      {getApi ? (
        <Script
          id="yotpo-api-script"
          src={`https://cdn-loyalty.yotpo.com/loader/${EcEnv.NEXT_PUBLIC_YOTPO_GUID}.js`}
          strategy="afterInteractive"
          async
        />
      ) : (
        <Script
          id="yotpo-widget-script"
          src={`https://cdn-widgetsrepository.yotpo.com/v1/loader/${EcEnv.NEXT_PUBLIC_YOTPO_GUID}`}
          async
          onReady={() => {
            // 延迟初始化，确保 DOM 已准备好
            const initializeWidgets = () => {
              try {
                // 检查是否有必要的容器元素存在
                const hasWidgetContainers = document.querySelector('[data-yotpo-widget-id]') !== null;
                if (hasWidgetContainers || document.readyState === 'complete') {
                  window?.yotpoWidgetsContainer?.initWidgets?.();
                } else {
                  // 如果容器还不存在，延迟重试
                  setTimeout(initializeWidgets, 100);
                }
              } catch (error) {
                logger.error('Failed to initialize Yotpo widgets', { error });
              }
            };

            // 使用 requestAnimationFrame 确保在下一帧执行
            requestAnimationFrame(() => {
              setTimeout(initializeWidgets, 200);
            });
          }}
        />
      )}
    </>
  );
};
